import { useEffect, useState, useMemo, useRef, memo } from "react";
import { AbsDropDown, Input, Label } from "abs-ui-components";
import { Controller, useFormContext } from "react-hook-form";
import { MDBCol, MDBRow } from "mdbreact";
import {
    getFallbackValue,
    FIELD_GROUPS,
    FIXED_FIELD_GROUP,
    DEFAULT_VALUES,
    mapFuelToFormValues
} from "./Helper";

/* -------------------------------------------------------------------------------------------------
 * Small Pure Utility Helpers
 * ------------------------------------------------------------------------------------------------- */
const normalizeCSLIP = (value) => {
    if (value === "TRUE" || value === 1 || value === "1") return "1";
    if (value === "FALSE" || value === 0 || value === "0") return "0";
    return null;
};

const cSlipOptions = [
    { label: "Applicable", value: "1" },
    { label: "Not Applicable", value: "0" },
];

/* -------------------------------------------------------------------------------------------------
 * Dropdown Helpers
 * ------------------------------------------------------------------------------------------------- */
const useFuelDropdowns = (baseFuelList) =>
    useMemo(() => {
        const group = (type) => ({
            category: type,
            values: baseFuelList
                ?.filter(f => f.categoryType === type)
                .sort((a, b) => a.code.localeCompare(b.code))
                .map(f => ({ label: f.code, value: f.code })) ?? []
        });
        return [group("Fossil"), group("Biofuel"), group("e-Fuel")];
    }, [baseFuelList]);

/* -------------------------------------------------------------------------------------------------
 * Fuel Meta Helper
 * ------------------------------------------------------------------------------------------------- */
const resolveFuel = (code, classificationType, selectedFuel, baseFuelList) => {
    if (!code) return null;

    if (selectedFuel?.code === code &&
        selectedFuel?.classificationType === classificationType
    ) return selectedFuel;

    return (
        selectedFuel?.childFuels?.find(
            f => f.code === code && f.classificationType === classificationType
        ) ||
        baseFuelList?.find(
            f => f.code === code && f.categoryType === classificationType
        )
    );
};

/* -------------------------------------------------------------------------------------------------
 * Main Component
 * ------------------------------------------------------------------------------------------------- */
const FuelForm = ({
    selectedFuel,
    fuelClassification,
    baseFuelList,
    isBlendComponent,
    formIndex,
    isEditMode,
    numberOfForms = 2,
}) => {
    const { control, getValues, setValue, watch, reset } = useFormContext();


    // Setup
    const isChild = isBlendComponent || (fuelClassification === "Blend" && formIndex !== undefined);
    const path = isChild ? `childFuels[${formIndex}].` : "";
    const getFieldName = (field) => `${path}${field}`;

    const [fieldMetaTypes, setFieldMetaTypes] = useState(selectedFuel?.metaTypeMap ?? {});
    const prevValues = useRef(selectedFuel || {});
    const firstRun = useRef(true);

    // Watch Form Fields
    const [
        fuelCode,
        classificationType,
        massMt,
        imoLcv,
        energyContentMj,
        eValue,
        imoCfCo2,
        ukMrvEfCo2,
        percentMass,
        percentEnergy
    ] = watch([
        `${path}code`,
        `${path}classificationType`,
        `${path}massMt`,
        `${path}imoLcv`,
        `${path}energyContentMj`,
        `${path}eValue`,
        `${path}imoCfCo2`,
        `${path}ukMrvEfCo2`,
        `${path}percentMass`,
        `${path}percentEnergy`
    ]);

    // Memoized dropdowns
    const dropdowns = useFuelDropdowns(baseFuelList);
    const fuelCodeOptions = useMemo(
        () => dropdowns.find((x) => x.category === classificationType)?.values ?? [],
        [dropdowns, classificationType]
    );
    const isFuelSelected = Boolean(classificationType && fuelCode);
    // Field meta types
    const getMetaType = (key) =>
        fieldMetaTypes[key] ??
        fieldMetaTypes[key?.toUpperCase()] ??
        getFallbackValue(fieldMetaTypes, key) ??
        0;
    const isFieldEnabled = (metaType) => metaType === 1;

    // Form Update Helpers
    const updateForm = (fuel) => {
        const mappedFuel = fuel
            ? mapFuelToFormValues(fuel)
            : { ...DEFAULT_VALUES, classificationType };

        setFieldMetaTypes(mappedFuel?.metaTypeMap ?? {});

        const existing = getValues();
        if (isChild) {
            existing.childFuels[formIndex] = mappedFuel;
        } else {
            Object.assign(existing, mappedFuel);
        }

        reset({ ...existing }, { keepErrors: false, keepDirty: false });
    };

    const initFuel = () => {
        const resolved = resolveFuel(fuelCode, classificationType, selectedFuel, baseFuelList);
        setFieldMetaTypes(resolved?.metaTypeMap || {});
        prevValues.current = resolved || {};
        firstRun.current = false;
    };
    const clearBlendCalculatedFields = () => {
    setValue("imoLcv", "", { shouldValidate: false });
    setValue("euLcv", "", { shouldValidate: false });
    setValue("co2eq", "", { shouldValidate: false });
};


    // Effects: Fuel selection/update
    useEffect(() => {
        if (firstRun.current) return initFuel();
        const fuel = resolveFuel(fuelCode, classificationType, selectedFuel, baseFuelList);
        updateForm(fuel);
    }, [fuelCode, classificationType]);

    // Blend-dependent calculations
    const setMassPercentages = () => {
        if (formIndex == null || formIndex >= numberOfForms) return;

        let total = 0;
        const masses = Array.from({ length: numberOfForms }, (_, i) => {
            const m = Number(getValues(`childFuels[${i}].massMt`)) || 0;
            total += m;
            return m;
        });

        masses.forEach((m, i) =>
            setValue(
                `childFuels[${i}].percentMass`,
                total > 0 ? ((m / total) * 100).toString() : "0",
                { shouldValidate: true }
            )
        );
    };



    const setEnergyPercentages = () => {
        if (formIndex == null || formIndex >= numberOfForms) return;

        const energy = [];
        let total = 0;

        for (let i = 0; i < numberOfForms; i++) {
            const pct = Number(getValues(`childFuels[${i}].percentMass`)) || 0;
            const lcv = Number(getValues(`childFuels[${i}].imoLcv`)) || 0;
            const e = pct * lcv;
            energy.push(e);
            total += e;
        }

        energy.forEach((e, i) =>
            setValue(
                `childFuels[${i}].percentEnergy`,
                total > 0 ? ((e / total) * 100).toString() : "0",
                { shouldValidate: true }
            )
        );
    };

    const setLcvs = () => {
        if (formIndex == null || formIndex >= numberOfForms) return;

        for (let i = 0; i < numberOfForms; i++) {
            const e = Number(getValues(`childFuels[${i}].energyContentMj`)) || 0;
            const m = Number(getValues(`childFuels[${i}].massMt`)) || 0;
            const classification = getValues(`childFuels[${i}].classificationType`);

            if (classification === "Biofuel") {
                if (e && m) {
                    const lcv = e / (m * 1_000_000);
                    const val = lcv.toString();
                    setValue(`childFuels[${i}].imoLcv`, val, { shouldValidate: true });
                    setValue(`childFuels[${i}].euLcv`, val, { shouldValidate: true });
                } else {
                    setValue(`childFuels[${i}].imoLcv`, "calculated");
                    setValue(`childFuels[${i}].euLcv`, "calculated");
                }
            }
        }
    };

   const setRootLcvs = () => {
    const classification = getValues(`classificationType`);
    if (classification === "Blend") {
        clearBlendCalculatedFields();
        return;
    }

    if (classification === "Biofuel") {
        const e = Number(getValues(`energyContentMj`)) || 0;
        const m = Number(getValues(`massMt`)) || 0;

        if (e && m) {
            const lcv = e / (m * 1_000_000);
            const val = lcv.toString();
            setValue(`imoLcv`, val);
            setValue(`euLcv`, val);
        } else {
            setValue(`imoLcv`, "");
            setValue(`euLcv`, "");
        }
    }
};

    const setCo2Eq = () => {
        if (formIndex == null || formIndex >= numberOfForms) return;

        for (let i = 0; i < numberOfForms; i++) {
            const classification = getValues(`childFuels[${i}].classificationType`);
            if (!["Biofuel", "e-Fuel"].includes(classification)) continue;

            const eVal = Number(getValues(`childFuels[${i}].eValue`)) || 0;
            const ef = Number(getValues(`childFuels[${i}].euMrvEfCo2`)) ?? 0;
            const lcv = Number(getValues(`childFuels[${i}].imoLcv`)) || 0;
            if (eVal && ef && lcv) {
                const co2 = (eVal - (ef / lcv)).toString();
                setValue(`childFuels[${i}].co2eq`, co2, { shouldValidate: true });
            } else {
                setValue(`childFuels[${i}].co2eq`, "calculated");
            }
        }
    };
    const setRootCo2Eq = () => {
    const classification = getValues("classificationType");
    if (classification === "Blend") {
        setValue("co2eq", "", { shouldValidate: false });
        return;
    }

    if (!["Biofuel", "e-Fuel"].includes(classification)) return;

    const eVal = Number(getValues("eValue")) || 0;
    const ef = Number(getValues("ukMrvEfCo2")) || 0;
    const lcv = Number(getValues("imoLcv")) || 0;

    if (eVal && ef && lcv) {
        setValue("co2eq", (eVal - ef / lcv)).toString();
    } else {
        setValue("co2eq", "");
    }
};


    const setParentCo2s = () => {
        if (formIndex == null || formIndex >= numberOfForms) return;

        let imo = 0;
        let uk = 0;
        let hasAnyValue = false;

        for (let i = 0; i < numberOfForms; i++) {
            const cfRaw = getValues(`childFuels[${i}].imoCfCo2`);
            const efRaw = getValues(`childFuels[${i}].ukMrvEfCo2`);
            const energyPercentRaw = getValues(`childFuels[${i}].percentEnergy`);
            const massPercentraw = getValues(`childFuels[${i}].percentMass`);
 
            const cf = Number(cfRaw) || 0;
            const ef = Number(efRaw) || 0;
            const energyPercent = Number(energyPercentRaw) || 0;
            const massPercent = Number(massPercentraw) || 0;
 
            if (cfRaw || efRaw || energyPercent || massPercent) {
                hasAnyValue = true;
            }
 
            const w1 = energyPercent / 100;
            const w2 = massPercent / 100;
            imo += w1 * cf;
            uk += w2 * ef;
        }
 

        if (!hasAnyValue) {
            setValue("imoCfCo2", "", { shouldValidate: false });
            setValue("ukMrvEfCo2", "", { shouldValidate: false });
            return;
        }

        setValue("imoCfCo2", imo ? imo.toString() : "", { shouldValidate: false });
        setValue("ukMrvEfCo2", uk ? uk.toString() : "", { shouldValidate: false });
    };


    // Dependency Watcher
    const setDependencies = () => {
        const prev = prevValues.current;
        const curr = {
            ...prev,
            code: fuelCode,
            classificationType,
            massMt,
            imoLcv,
            energyContentMj,
            eValue,
            imoCfCo2,
            ukMrvEfCo2,
            percentMass,
            percentEnergy
        };

        if (fuelClassification === "Blend") {
            if (prev.massMt !== curr.massMt) setMassPercentages();

            if (
                prev.energyContentMj !== curr.energyContentMj ||
                prev.massMt !== curr.massMt
            ) {
                setLcvs();
            }

            if (
                prev.percentMass !== curr.percentMass ||
                prev.imoLcv !== curr.imoLcv
            ) {
                setEnergyPercentages();
            }

            if (
                prev.eValue !== curr.eValue ||
                prev.ukMrvEfCo2 !== curr.ukMrvEfCo2 ||
                prev.imoLcv !== curr.imoLcv
            ) {
                setCo2Eq();
            }

            if (
                prev.percentEnergy !== curr.percentEnergy ||
                prev.imoCfCo2 !== curr.imoCfCo2 ||
                prev.ukMrvEfCo2 !== curr.ukMrvEfCo2
            ) {
                setParentCo2s();
            }
        } else {
            // ROOT (Biofuel / e-Fuel)
            if (
                prev.energyContentMj !== curr.energyContentMj ||
                prev.massMt !== curr.massMt
            ) {
                setRootLcvs();
            }

            if (
                prev.eValue !== curr.eValue ||
                prev.ukMrvEfCo2 !== curr.ukMrvEfCo2 ||
                prev.imoLcv !== curr.imoLcv
            ) {
                setRootCo2Eq();
            }
        }

        prevValues.current = curr;
    };

    useEffect(() => {
        setDependencies();
    }, [
        massMt,
        imoLcv,
        energyContentMj,
        eValue,
        imoCfCo2,
        ukMrvEfCo2,
        percentMass,
        percentEnergy
    ]);


    // RENDER
    return (
        <div className="fuel-registry mt-2">
            <form>

                {/*  Classification + Fuel Code */}
                <MDBRow className="mb-4" noGutters>
                    <MDBCol md="6">
                        {fuelClassification === "Blend" && (
                            <>
                                <Label bold name="Fuel Classification" />
                                <Controller
                                    name={getFieldName("classificationType")}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ width: "200px" }}>
                                            <AbsDropDown
                                                options={dropdowns.map(d => ({ label: d.category, value: d.category }))}
                                                selected={field.value}
                                                handleChange={(val) => field.onChange(val?.value ?? "")}
                                                placeholder="Select Fuel Classification"
                                                isClearable
                                            />
                                        </div>
                                    )}
                                />
                            </>
                        )}
                    </MDBCol>

                    <MDBCol md={fuelClassification === "Blend" ? "6" : "12"}>
                        <Label bold required name="Fuel Code" className="mb-1" />
                        <Controller
                            name={getFieldName("code")}
                            control={control}
                            rules={{ required: "Fuel Code is required" }}
                            render={({ field, fieldState }) => (
                                <>
                                    <div style={{ width: "200px" }}>
                                        <AbsDropDown
                                            options={fuelCodeOptions}
                                            selected={field.value}
                                            handleChange={(sel) => field.onChange(sel?.value ?? "")}
                                            placeholder="Select Fuel Code"
                                            isClearable={!isEditMode}
                                            disabled={fuelClassification !== "Blend" && isEditMode}
                                        />
                                    </div>
                                    {fieldState.error && <div className="error-msg">{fieldState.error.message}</div>}
                                </>
                            )}
                        />
                    </MDBCol>
                </MDBRow>

                {/* Fixed Fields */}
                <MDBRow className="mb-4">
                    {FIXED_FIELD_GROUP.map(({ name, key }, i) => {
                        const isRequired = key === "massMt" && (fuelClassification === "Blend" || fuelClassification === "Biofuel");

                        const disable =
                            ["percentMass", "percentEnergy"].includes(key) ||
                            key === "name" ||
                            (key === "massMt" && !isFuelSelected);

                        const placeholder =
                            [].includes(key) &&
                                fuelClassification !== "Blend"
                                ? "Not Applicable"
                                : undefined;

                        return (
                            <MDBCol key={i}>
                                <Label bold required={isRequired} name={name} className="mb-1" />

                                <Controller
                                    name={getFieldName(key)}
                                    control={control}
                                    rules={isRequired ? { required: `${name} is required` } : undefined}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <div style={{ width: "150px" }}>
                                                <Input
                                                    {...field}
                                                    type={key === "massMt" ? "number" : "text"}
                                                    disabled={disable}
                                                    placeholder={placeholder}
                                                    variant="outlined"
                                                    size="small"
                                                    onKeyDown={(e) => {
                                                        if (
                                                            key === "massMt" &&
                                                            (e.key === "+" || e.key === "-")
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {fieldState.error && (
                                                <div className="error-msg">{fieldState.error.message}</div>
                                            )}
                                        </>
                                    )}
                                />
                            </MDBCol>
                        );
                    })}
                </MDBRow>

                {/*  Dynamic FIELD_GROUPS */}
                {FIELD_GROUPS.map((group, idx) => (
                    <MDBRow className="mb-4" key={idx}>
                        {group.map(({ name, key, required }) => {
                            const metaType = getMetaType(key);
                            const enabled = isFieldEnabled(metaType);
                            const alwaysDisabled = ["rwd", "cSlip"].includes(key);
                            const blendNotApplicable = [].includes(key) && fuelClassification !== "Blend";
                            const isFossil = classificationType === "Fossil";
                            const fossilNotApplicable = isFossil && ["energyContentMj", "eValue"].includes(key);
                            const notApplicable = blendNotApplicable || fossilNotApplicable;

                            const disable = ["percentMass", "percentEnergy"].includes(key) || notApplicable;

                            const placeholder =
                                metaType === 2
                                    ? "Calculated"
                                    : notApplicable
                                        ? "Not Applicable"
                                        : undefined;

            // Set required only if not fossil or explicitly required
            const isRequired = (required || (key === "energyContentMj" && (fuelClassification === "Blend" || fuelClassification === "Biofuel"))) && !isFossil;

                            return (
                                <MDBCol md={3} key={key}>
                                    <Label
                                        bold
                                        required={isRequired}
                                        name={name}
                                        className="mb-1"
                                    />

                                    <Controller
                                        name={getFieldName(key)}
                                        control={control}
                                        rules={
                                            isRequired
                                                ? {
                                                    required: `${name} is required`,
                                                    min: { value: 0 }
                                                }
                                                : {}
                                        }
                                        render={({ field, fieldState }) => (
                                            <>
                                                <div style={{ width: "150px" }}>
                                                    {key === "cSlip" ? (
                                                        <AbsDropDown
                                                            options={cSlipOptions}
                                                            selected={normalizeCSLIP(field.value)}
                                                            handleChange={(sel) => field.onChange(sel?.value ?? null)}
                                                            placeholder="Select C Slip"
                                                            isClearable
                                                            disabled={alwaysDisabled || !enabled}
                                                        />
                                                    ) : (
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            disabled={alwaysDisabled || !enabled || disable}
                                                            placeholder={placeholder}
                                                            variant="outlined"
                                                            size="small"
                                                            onKeyDown={(e) => {
                                                                if (["+", "-"].includes(e.key)) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>

                                                {fieldState.error && (
                                                    <div className="error-msg">{fieldState.error.message}</div>
                                                )}
                                            </>
                                        )}
                                    />
                                </MDBCol>
                            );
                        })}

                        {/* Fill empty columns in group */}
                        {group.length < 4 &&
                            Array.from({ length: 4 - group.length }).map((_, i) => (
                                <MDBCol md={3} key={i} />
                            ))}
                    </MDBRow>
                ))}


            </form>
        </div>
    );
}
export default memo(FuelForm);